// 9fbef606107a605d69c0edbcd8029e5d
import { PropTypes } from 'prop-types';
import Carousel from '@tcp/core/src/components/common/molecules/Carousel';
import { getIconPath, getViewportInfo } from '@tcp/core/src/utils';
import React from 'react';
import { breakpoints } from '@tcp/core/styles/themes/TCP/mediaQuery';
import LabeledRadioButton from '@tcp/core/src/components/common/molecules/LabeledRadioButton';
import cssClassName from '@tcp/core/src/utils/cssClassName';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import withStyles from '../../../hoc/withStyles';
import style from '../styles/FilterPills.style';

import Button from '../../Button';
import RatingPills from '../../RatingPills/views/RatingPills';

// returns the index (or indices) of the item(s) with the given value(s) in the given optionsMap
function getIndexOrIndicesOfValue(optionsMap, valueOrValues) {
  return Array.isArray(valueOrValues)
    ? optionsMap.map(
        (item) => valueOrValues.findIndex((selectedValue) => item.value === selectedValue) >= 0
      )
    : optionsMap.findIndex((item) => item.value === valueOrValues);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const FilterPills = (props) => {
  const {
    className,
    optionsMap,
    input,
    isLoadingMore,
    formValues,
    setSelectedFilterName,
    placeholder,
    ratingsFilter,
    ratingLabel,
    outOfLabel,
    isTileRedesignAB,
    handleSubmitForm,
  } = props;
  const hasMounted = useHasMounted();

  // select the item with the given value
  const setValue = (selectedValue, name = '') => {
    const {
      allowMultipleSelections,
      input: { value, onChange, name: fiterName },
    } = props;

    if (allowMultipleSelections) {
      let newValue = [...value];
      if (formValues?.v_tcpfit_unbxd_uFilter) {
        newValue = formValues?.v_tcpfit_unbxd_uFilter.filter((item) => item === value);
      }
      newValue.push(selectedValue);
      onChange(newValue);
      handleSubmitForm(fiterName, newValue);
    } else {
      if (name === 'TCPBazaarVoiceRating_inText_uFilter') {
        if (value !== selectedValue) {
          onChange([selectedValue]);
          handleSubmitForm(fiterName, [selectedValue]);
        }
      } else if (value !== selectedValue) {
        onChange(selectedValue);
        handleSubmitForm(fiterName, selectedValue);
      }
      if (!value);
    }
  };

  const unsetValue = (value) => {
    const newValues = input.value.filter((val) => val !== value);
    // notify our listeners that the user wants the value of this component to change
    input.onChange(newValues);
    handleSubmitForm(input.name, newValues);
  };

  const setItemValue = (onFilterSelection, clickedItemValue, name = '') => {
    setValue(clickedItemValue, name);
  };

  // handles mouse clicks on items in the dropdown
  const handleItemClick = (event, clickedItemIndex) => {
    const { allowMultipleSelections, onFilterSelection, isFavoriteView } = props;
    if (!optionsMap[clickedItemIndex]?.disabled) {
      const clickedItemValue = optionsMap[clickedItemIndex]?.value;
      const selectedIndex = getIndexOrIndicesOfValue(optionsMap, input?.value);
      const isFavoriteCall = isFavoriteView
        ? allowMultipleSelections && selectedIndex[clickedItemIndex] && !onFilterSelection
        : allowMultipleSelections && selectedIndex[clickedItemIndex];
      if (isFavoriteCall) {
        unsetValue(clickedItemValue); // remove clickedItemValue from this component's selcted values list
      } else {
        // set the value (or add to the value if multiple selections is on) of this component to clickedItemValue
        setItemValue(onFilterSelection, clickedItemValue, optionsMap?.[clickedItemIndex]?.name);
      }
    }
  };

  const getRatingFromString = (ratingText) => {
    const [first, ...second] = ratingText?.split(' ');

    return { first, second: second.join(' ')?.replace('and', '&') };
  };

  const ratingCompare = (r1, r2) => {
    if (r1.value < r2.value) {
      return 1;
    }
    if (r1.value > r2.value) {
      return -1;
    }
    return 0;
  };

  const getRatingPills = () => {
    const sortedRatings = optionsMap.sort(ratingCompare);
    return sortedRatings?.map((item, index) => {
      return (
        <div className="customer-rating-radio-buttons">
          <RatingPills
            handleItemClick={handleItemClick}
            item={item}
            index={index}
            getRatingFromString={getRatingFromString}
            formValues={formValues}
            optionsMap={sortedRatings}
            placeholder={placeholder}
            ratingLabel={ratingLabel}
            outOfLabel={outOfLabel}
            isTileRedesignAB={isTileRedesignAB}
          />
        </div>
      );
    });
  };

  const getFitPills = () => {
    if (ratingsFilter) {
      return getRatingPills();
    }
    return optionsMap?.map((item, index) => {
      return (
        <div className="fit-pill">
          <Button
            className="pill-click"
            onClick={(e) => {
              e.preventDefault();
              setTimeout(() => {
                setSelectedFilterName(placeholder);
                handleItemClick(e, index);
              }, 0);
            }}
          >
            <LabeledRadioButton
              key={item.value}
              id={index}
              name={item.value}
              value={item?.value}
              checked={formValues?.v_tcpfit_unbxd_uFilter?.includes(item?.value)}
              selectedValue={item.value}
              tabIndex="0"
              input={formValues[optionsMap[index]?.name]?.includes(item?.value)}
              disabled={item.disabled}
              newQVDesign={true}
              isNewPDPEnabled={true}
              isPLPFilterRedesign={true}
              className={cssClassName(
                `${className}-item `,
                className,
                { ' item-selected-option': item.value === item.name },
                { ' item-disabled-option': item.disabled }
              )}
            >
              {item.title}
            </LabeledRadioButton>
          </Button>
        </div>
      );
    });
  };

  const slidesToShow = getViewportInfo()?.width >= 1920 ? 4.5 : 2.5;
  const isDesktop = hasMounted ? getViewportInfo()?.isDesktop : false;

  return (
    <section
      type="checkbox"
      className={`${className} ${isLoadingMore ? 'disable-list-items' : ''} filter-fit-main `}
    >
      <div className="size-and-fit-detail-item fitFilter">
        {isDesktop && !ratingsFilter ? (
          <Carousel
            options={{
              autoplay: false,
              arrows: true,
              fade: false,
              swipeToSlide: true,
              dots: false,
              swipe: true,
              slide: true,
              slidesToShow,
              infinite: false,
              slidesToScroll: 1,
              responsive: [
                {
                  breakpoint: breakpoints.large,
                  settings: {
                    slidesToShow,
                    dots: false,
                  },
                },
                {
                  breakpoint: breakpoints.medium,
                  settings: {
                    slidesToShow,
                    arrows: false,
                  },
                },
                {
                  breakpoint: breakpoints.small,
                  settings: {
                    slidesToShow,
                    arrows: false,
                  },
                },
              ],
            }}
            carouselConfig={{
              autoplay: false,
              customArrowLeft: getIconPath('family-outfit-arrow'),
              customArrowRight: getIconPath('family-outfit-arrow'),
            }}
          >
            {getFitPills()}
          </Carousel>
        ) : (
          getFitPills()
        )}
      </div>
    </section>
  );
};

FilterPills.propTypes = {
  className: PropTypes.string.isRequired,
  optionsMap: PropTypes.shape([]),
  input: PropTypes.shape({ value: {}, onChange: () => {} }),
  isLoadingMore: PropTypes.bool,
  formValues: PropTypes.shape({}),
  allowMultipleSelections: PropTypes.bool,
  onFilterSelection: PropTypes.func,
  isFavoriteView: PropTypes.bool,
  placeholder: PropTypes.string,
  setSelectedFilterName: PropTypes.func,
};

FilterPills.defaultProps = {
  optionsMap: [],
  input: {},
  isLoadingMore: false,
  formValues: {},
  allowMultipleSelections: false,
  onFilterSelection: () => {},
  isFavoriteView: false,
  placeholder: '',
  setSelectedFilterName: () => {},
};

export default withStyles(FilterPills, style);
export { FilterPills as FilterPillsVanilla };
